import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import SEOHead from "@/components/SEOHead";
import SEORelatedPages from "@/components/SEORelatedPages";
import { getSEOData } from "@/lib/seo-pages";

const seo = getSEOData("/pesnya-v-podarok")!;

const faqItems = [
  { q: "Сколько стоит персональная песня?", a: "Демоверсия бесплатна. Полная версия — от 249 ₽." },
  { q: "Можно ли выбрать жанр?", a: "Да — доступны поп, рок, рэп, романтика, шансон, электро и другие стили." },
  { q: "Как быстро будет готова песня?", a: "Обычно создание занимает не более 3 минут." },
  { q: "Нужно ли устанавливать приложение?", a: "Нет. Создание доступно онлайн без установки — с телефона или компьютера." },
  { q: "Можно ли подарить песню удалённо?", a: "Да — вы получаете ссылку, которую можно отправить в мессенджере или подарить через красивую подарочную страницу." },
];

const PesnyaVPodarok = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead title={seo.title} description={seo.description} canonical={seo.canonical} jsonLd={{ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faqItems.map((item) => ({ "@type": "Question", name: item.q, acceptedAnswer: { "@type": "Answer", text: item.a } })) }} />
      <Navbar />
      <main className="pt-24 pb-16">
        <article className="container px-4 mx-auto max-w-3xl">
          <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold mb-6 text-foreground leading-tight">
            Песня в подарок — закажи уникальный музыкальный сюрприз
          </h1>

          <div className="space-y-4 text-muted-foreground leading-relaxed mb-10">
            <p>Полночь. Вы сидите перед экраном и думаете: «Что подарить человеку, у которого всё есть?» Носки — банально. Сертификат — безлично. А потом вспоминаете ту историю, как вы вместе застряли в лифте, как она впервые попробовала суши и чихнула от васаби.</p>
            <p>Вы вводите эту историю в HitChef. Через три минуты слышите <strong className="text-foreground">песню, в которой звучит именно ваша история</strong>. С именем, деталями и музыкой, которая попадает точно в настроение.</p>
            <p><strong className="text-foreground">Персональная песня в подарок</strong> — это не просто трек. Это эмоция, упакованная в музыку. Подарок, который невозможно купить в магазине и невозможно забыть.</p>
          </div>

          <div className="my-10 text-center">
            <Button size="lg" className="shadow-soft rounded-full px-8 text-base" asChild>
              <Link to="/create">🔥 Создать песню в подарок</Link>
            </Button>
            <p className="text-muted-foreground text-sm mt-3">Демоверсия бесплатна. Без установки приложений.</p>
          </div>

          <section className="mb-12">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-4 text-foreground">Почему песня — лучший подарок?</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>На полке стоят десятки подарков. Через год вы не вспомните, от кого была та кружка. Но песню с вашей историей — вы будете переслушивать годами.</p>
              <ul className="space-y-2">
                <li>🎁 <strong className="text-foreground">Уникальный</strong> — такого подарка больше ни у кого нет. Песня существует в единственном экземпляре.</li>
                <li>❤️ <strong className="text-foreground">Эмоциональный</strong> — вызывает мурашки, смех или слёзы радости.</li>
                <li>🎵 <strong className="text-foreground">Вечный</strong> — можно переслушивать, ставить на звонок, включать на праздниках.</li>
                <li>⚡ <strong className="text-foreground">Быстрый</strong> — готов за 3 минуты, даже если праздник уже завтра.</li>
              </ul>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-4 text-foreground">На какой праздник подарить песню?</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>Персональная песня подходит для любого события — главное, чтобы был человек, которого вы хотите удивить:</p>
              <ul className="space-y-2">
                <li>🎂 <Link to="/pesnya-na-den-rozhdeniya" className="text-primary hover:underline">День рождения</Link> — самый популярный повод</li>
                <li>💍 <Link to="/pesnya-na-svadbu" className="text-primary hover:underline">Свадьба</Link> или <Link to="/pesnya-na-godovshchinu" className="text-primary hover:underline">годовщина</Link></li>
                <li>🌷 8 марта или 23 февраля</li>
                <li>🎄 Новый год</li>
                <li>🎓 <Link to="/pesnya-na-vypusknoj" className="text-primary hover:underline">Выпускной</Link> или <Link to="/pesnya-na-yubiley" className="text-primary hover:underline">юбилей</Link></li>
                <li>💝 Или просто так — чтобы сказать «ты важен для меня»</li>
              </ul>
            </div>
          </section>

          <section className="mb-12">
            <h3 className="font-display text-lg sm:text-xl font-bold mb-3 text-foreground">«Она заплакала. А потом поставила песню на повтор»</h3>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>Настя хотела подарить маме что-то особенное на 60-летие. Написала историю: как мама учила её кататься на велосипеде, как пекла пироги каждое воскресенье, как провожала в первый класс.</p>
              <p>Выбрала мягкую балладу. Через три минуты — песня была готова. На празднике мама дослушала до конца, обняла Настю и прошептала: «Это лучший подарок за всю мою жизнь».</p>
              <p>А потом поставила песню на повтор. Три раза подряд.</p>
            </div>
          </section>

          <div className="my-10 text-center">
            <Button size="lg" className="shadow-soft rounded-full px-8 text-base" asChild>
              <Link to="/create">🎵 Создать подарок, который запомнится</Link>
            </Button>
            <p className="text-muted-foreground text-sm mt-3">Без регистрации. Без приложений.</p>
          </div>

          <section className="mb-12">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-4 text-foreground">Кому подарить песню?</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p><strong className="text-foreground"><Link to="/pesnya-devushke-v-podarok" className="text-primary hover:underline">Девушке или жене</Link>.</strong> Романтическая песня с вашей историей любви — вместо тысячи слов.</p>
              <p><strong className="text-foreground"><Link to="/pesnya-mame-v-podarok" className="text-primary hover:underline">Маме или папе</Link>.</strong> Тёплая песня от детей — с воспоминаниями и благодарностью.</p>
              <p><strong className="text-foreground"><Link to="/pesnya-pro-druga" className="text-primary hover:underline">Другу</Link>.</strong> Весёлый хит с общими историями и внутренними шутками.</p>
              <p><strong className="text-foreground"><Link to="/pesnya-kollege-v-podarok" className="text-primary hover:underline">Коллеге</Link>.</strong> Корпоративный хит с рабочими приколами.</p>
              <p><strong className="text-foreground"><Link to="/pesnya-rebenku-v-podarok" className="text-primary hover:underline">Ребёнку</Link>.</strong> Волшебная песня с его именем и мечтами.</p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-4 text-foreground">Как заказать песню в подарок?</h2>
            <ol className="space-y-3 text-muted-foreground list-decimal list-inside leading-relaxed">
              <li><strong className="text-foreground">Нажмите «Создать хит»</strong> — откроется простая форма</li>
              <li><strong className="text-foreground">Выберите событие и жанр</strong> — от нежной баллады до энергичного рэпа</li>
              <li><strong className="text-foreground">Расскажите историю</strong> — имя, забавные моменты, тёплые слова</li>
              <li><strong className="text-foreground">Получите демоверсию</strong> — бесплатно, за 3 минуты</li>
            </ol>
            <p className="text-muted-foreground mt-4">После прослушивания демо вы можете получить полную версию песни — и оформить красивую <Link to="/originalnyi-podarok" className="text-primary hover:underline">подарочную страницу</Link> со своим посланием.</p>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-6 text-foreground">Часто задаваемые вопросы</h2>
            <Accordion type="single" collapsible className="space-y-2">
              {faqItems.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border border-border rounded-xl px-5 bg-card">
                  <AccordionTrigger className="text-left hover:no-underline text-sm font-medium text-foreground">{item.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm leading-relaxed">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <div className="my-10 text-center">
            <Button size="lg" className="shadow-soft rounded-full px-8 text-base" asChild>
              <Link to="/create">🎶 Подарите музыку, а не вещи</Link>
            </Button>
            <p className="text-muted-foreground text-sm mt-3">Персональная песня — подарок, которого достоин каждый.</p>
          </div>

          <SEORelatedPages pages={seo.relatedPages || []} />
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default PesnyaVPodarok;
